import {
	Component,
	DestroyRef,
	ElementRef,
	HostBinding,
	inject,
	Input,
	OnInit,
	signal,
	WritableSignal,
	ChangeDetectionStrategy,
	forwardRef,
	input,
	viewChild,
} from '@angular/core';
import { LayoutService } from '../../services/layout.service';
import { ConfigService } from '../../config/config.service';
import { map, startWith, switchMap } from 'rxjs/operators';
import { NavigationService } from '../../services/navigation.service';
import { PopoverService } from '../../components/popover/popover.service';
import { MegaMenuComponent } from '../../components/mega-menu/mega-menu.component';
import { Observable, of, Subscription } from 'rxjs';
import { JobLog, JobLogService, ViewQuery } from '@eadsysteme/joblogclient-v1';
import {
	MatTableDataSource,
	MatTable,
	MatColumnDef,
	MatHeaderCellDef,
	MatHeaderCell,
	MatCellDef,
	MatCell,
	MatHeaderRowDef,
	MatHeaderRow,
	MatRowDef,
	MatRow,
	MatNoDataRow,
} from '@angular/material/table';
import { WebSocketSubject } from 'rxjs/internal/observable/dom/WebSocketSubject';
import { TranslocoLocaleService } from '@jsverse/transloco-locale';
import { TranslocoService, TranslocoDirective } from '@jsverse/transloco';
import { KeycloakService } from 'src/app/auth/keycloak.service';
import { DevService } from '../../../app/services/dev.service';
import { SessionService } from '../../../app/services/session.service';
import { JobStateService } from '../../services/jobState.service';
import { saveAs } from 'file-saver';
import { Icons } from '../../../app/util/Icons';
import { fadeInUp400ms } from '../../animations/fade-in-up.animation';
import { stagger40ms } from '../../animations/stagger.animation';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { LocaleHelper } from '../../../app/util/LocaleHelper';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { Router, RouterLink } from '@angular/router';
import { QuickActions } from '../../../app/services/quick-actions';
import { MatIconButton, MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatMenuTrigger, MatMenu, MatMenuItem } from '@angular/material/menu';
import { CdkOverlayOrigin, CdkConnectedOverlay } from '@angular/cdk/overlay';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { ToolbarUserComponent } from './toolbar-user/toolbar-user.component';
import { NavigationComponent } from '../navigation/navigation.component';
import { MatTooltip } from '@angular/material/tooltip';
import { AraPaginatorComponent } from '../../../app/components/util/ara-paginator/ara-paginator.component';

import { DatePipe } from '@angular/common';
import JobTypeEnum = JobLog.JobTypeEnum;
import JobStatusEnum = JobLog.JobStatusEnum;

@Component({
	selector: 'vex-toolbar',
	templateUrl: './toolbar.component.html',
	styleUrls: ['./toolbar.component.scss'],
	animations: [fadeInUp400ms, stagger40ms],
	changeDetection: ChangeDetectionStrategy.Eager,
	imports: [
		TranslocoDirective,
		MatIconButton,
		MatIcon,
		RouterLink,
		MatMenuTrigger,
		MatButton,
		MatMenu,
		MatMenuItem,
		CdkOverlayOrigin,
		MatProgressSpinner,
		ToolbarUserComponent,
		NavigationComponent,
		CdkConnectedOverlay,
		MatTable,
		MatColumnDef,
		MatHeaderCellDef,
		MatHeaderCell,
		MatCellDef,
		MatCell,
		MatTooltip,
		MatHeaderRowDef,
		MatHeaderRow,
		MatRowDef,
		MatRow,
		MatNoDataRow,
		AraPaginatorComponent,
		forwardRef(() => ToolbarComponent),
		DatePipe,
	],
})
export class ToolbarComponent implements OnInit {
	layoutService = inject(LayoutService);
	icons = inject(Icons);
	jobStateService = inject(JobStateService);
	private configService = inject(ConfigService);
	private navigationService = inject(NavigationService);
	private popoverService = inject(PopoverService);
	private translocoLocaleService = inject(TranslocoLocaleService);
	private translocoService = inject(TranslocoService);
	private keycloakService = inject(KeycloakService);
	private devService = inject(DevService);
	protected sessionService = inject(SessionService);
	private jobLogService = inject(JobLogService);

	private readonly destroyRef = inject(DestroyRef);
	private readonly quickActions = inject(QuickActions);
	private readonly router = inject(Router);

	readonly mobileQuery = input<boolean>(undefined);

	@Input()
	@HostBinding('class.shadow-b')
	hasShadow: boolean;

	navigationItems = this.navigationService.items;

	isHorizontalLayout = toSignal(this.configService.config$.pipe(map((config) => config.layout === 'horizontal')));
	isVerticalLayout = toSignal(this.configService.config$.pipe(map((config) => config.layout === 'vertical')));
	isNavbarInToolbar = toSignal(this.configService.config$.pipe(map((config) => config.navbar.position === 'in-toolbar')));
	isNavbarBelowToolbar = toSignal(this.configService.config$.pipe(map((config) => config.navbar.position === 'below-toolbar')));
	userVisible = toSignal(this.configService.config$.pipe(map((config) => config.toolbar.user.visible)));

	megaMenuOpen$: Observable<boolean> = of(false);

	impersonationEvent$: Subscription;
	selectedLanguage: WritableSignal<string> = signal('');
	model: any = {};

	displayedColumns: string[] = ['startTime', 'jobType', 'percent'];
	dataSource = new MatTableDataSource<JobLog>();

	name: string;

	ws: WebSocketSubject<any>;
	tryRefreshJobs = true;
	isMobile: boolean;

	isOpen$: Observable<boolean> = this.jobStateService.isDialogOpen();
	isOpen = false;

	protected readonly JobStatusEnum = JobStatusEnum;
	protected readonly JobTypeEnum = JobTypeEnum;
	readonly paginator = viewChild(MatPaginator);
	pageSizeOptions: number[] = [5, 10, 50, 100];
	pageIndex: number = 0;
	pageSize: number = 5;
	length: number = 5;

	finishedLoading: boolean = false;

	constructor() {
		this.setupToolbar();
		this.isOpen$.subscribe((value) => {
			this.openJobDialogAndLoadData(value);
		});
		this.impersonationEvent$ = this.devService.getImpersonationEvent().subscribe(() => {
			this.setupToolbar();
			this.loadJobData();
			// The following line is mandatory to rebuild the side menu :)
			this.translocoService.setActiveLang(this.selectedLanguage());
		});
	}

	openQuickpanel(): void {
		this.layoutService.openQuickpanel();
	}

	openSidenav(): void {
		this.layoutService.openSidenav();
	}

	openMegaMenu(origin: ElementRef | HTMLElement): void {
		this.megaMenuOpen$ = of(
			this.popoverService.open({
				content: MegaMenuComponent,
				origin,
				offsetY: 12,
				position: [
					{
						originX: 'start',
						originY: 'bottom',
						overlayX: 'start',
						overlayY: 'top',
					},
					{
						originX: 'end',
						originY: 'bottom',
						overlayX: 'end',
						overlayY: 'top',
					},
				],
			})
		).pipe(
			switchMap((popoverRef) => popoverRef.afterClosed$.pipe(map(() => false))),
			startWith(true)
		);
	}

	openSearch(): void {
		this.layoutService.openSearch();
	}

	private static setFileName(element: JobLog) {
		return element.id + '_massendruck_' + element.startTime;
	}

	setupToolbar() {
		const language = localStorage.getItem('locale') ?? LocaleHelper.localeToLanguage(this.translocoService.getActiveLang()) ?? 'de';

		this.selectedLanguage.set(language);
		const tokenParsed = this.keycloakService.getKeycloakInstance().tokenParsed;
		this.isMobile = this.layoutService.isMobile();
		this.sessionService.checkLeadingSystem();
	}

	async ngOnInit() {
		await this.loadJobData();
	}

	changeLanguage(language: string) {
		this.selectedLanguage.set(language);
		this.translocoService.setActiveLang(language);
		this.translocoLocaleService.setLocale(LocaleHelper.languageToLocale(language));
		localStorage.setItem('locale', language);
	}

	downloadFileFromJobLog(element) {
		this.jobLogService
			.getContent(this.sessionService.effectiveArea, element.id)
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((value: Blob) => {
				let blob;
				if (element.fileType === 'ZIP') {
					blob = new Blob([value], { type: 'application/zip' });
				} else {
					blob = new Blob([value], { type: 'application/pdf' });
				}
				const fileName = ToolbarComponent.setFileName(element);
				saveAs(blob, fileName);
			});
	}

	delay(ms: number) {
		return new Promise((resolve) => setTimeout(resolve, ms));
	}

	async loadJobData() {
		do {
			this.getJobLogs();
			await this.delay(3000);
		} while (!this.jobStateService.allJobsFinished && this.tryRefreshJobs);
	}

	getJobLogs() {
		this.finishedLoading = false;
		const viewQuery: ViewQuery = {
			page: this.pageIndex,
			size: this.pageSize,
		};
		this.jobLogService
			.getJobLogs(this.sessionService.effectiveArea, viewQuery)
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe({
				next: (jobLogsResponse: any) => {
					if (!jobLogsResponse || jobLogsResponse.totalElements === 0) {
						this.tryRefreshJobs = false;
						this.jobStateService.setAllJobsFinished(true);
					} else {
						this.dataSource.data = jobLogsResponse.items;
						this.length = jobLogsResponse.totalElements;
						this.pageIndex = jobLogsResponse.number;
						const jobsDone = [
							...new Set(
								this.dataSource.data.map((jobLog) => jobLog.jobStatus === JobStatusEnum.FINISHED || jobLog.jobStatus === JobStatusEnum.ERROR)
							),
						];
						this.jobStateService.setAllJobsFinished(jobsDone.length === 1 && jobsDone.includes(true));
					}
					this.finishedLoading = true;
				},
				error: () => {
					this.tryRefreshJobs = false;
					this.finishedLoading = false;
					this.jobStateService.setAllJobsFinished(true);
				},
			});
	}

	private async openJobDialogAndLoadData(value: boolean) {
		this.isOpen = value;
		if (this.isOpen) {
			await this.delay(1000);
			this.tryRefreshJobs = true;
			this.loadJobData();
		}
	}

	getPaginatedData($event: PageEvent) {
		this.pageIndex = $event.pageIndex;
		this.pageSize = $event.pageSize;
		this.getJobLogs();
	}

	protected newTenantChange() {
		this.quickActions.openTenantChange();
	}

	protected newVacancyChange() {
		this.quickActions.openVacancyChange();
	}

	protected changeUviPermission() {
		this.quickActions.changeUviPermission();
	}

	protected activateRealEstate() {
		this.router.navigate(['/permissions']);
	}
}
